import { apiClient } from './client';

export type FilterRuleField = 'from' | 'from_domain' | 'subject' | 'body';
export type FilterRuleMatch = 'contains' | 'equals' | 'regex';

export interface FilterRule {
  id: string;
  field: FilterRuleField;
  match: FilterRuleMatch;
  value: string;
  enabled: boolean;
}

/** Keyword routing for the new-ticket staff alert: when an inbound email's
 *  subject contains any keyword, these recipients get the alert instead of
 *  the company default notification list. First enabled match wins. */
export interface NotifyRoutingRule {
  id: string;
  keywords: string[];
  emails: string[];
  enabled: boolean;
}

/** Repeat-alert collapsing: a recurring alert with the same normalized
 *  subject from the same sender is appended to the still-open ticket
 *  instead of opening another one. */
export interface CollapseSettings {
  enabled?: boolean;
  windowHours?: number;
  machineOnly?: boolean;
}

export interface EmailIntegrationSettings {
  autoReply?: boolean;
  autoReplyTemplate?: string;
  aliases?: string[];
  allowedDomains?: string[];
  spamScoreThreshold?: number;
  /** Senders (address or bare domain) that bypass spam scoring entirely. */
  spamAllowlist?: string[];
  /** Tenant-authored suppression rules — matching mail never becomes a ticket. */
  filterRules?: FilterRule[];
  /** Triage alert recipients when an email ticket has no product line. */
  unmatchedNotifyEmails?: string[];
  /** Keyword routing for the new-ticket staff alert. */
  notifyRoutingRules?: NotifyRoutingRule[];
  /** Repeat-alert collapsing. */
  collapseRepeats?: CollapseSettings;
}

export interface EmailIntegration {
  id: string;
  inboundEmail: string;
  defaultCategoryId: string | null;
  defaultPriority: string;
  isActive: boolean;
  settings: EmailIntegrationSettings;
}

export interface SaveEmailIntegrationInput {
  inboundEmail: string;
  defaultCategoryId?: string | null;
  defaultPriority?: string;
  isActive?: boolean;
  settings?: EmailIntegrationSettings;
}

/** One contributor to a message's spam score. Positive points push toward
 *  quarantine; negative points are trust signals (known sender, DMARC pass). */
export interface SpamSignal {
  name: string;
  points: number;
  detail?: string;
}

export interface InboundEmailLogRow {
  id: string;
  messageId: string | null;
  fromEmail: string;
  toEmail: string;
  subject: string;
  status: string;
  ticketId: string | null;
  ticketNumber: string | null;
  /** Set when the mail was routed to the CRM inbox instead of a ticket. */
  conversationId?: string | null;
  conversationNumber?: number | null;
  error: string | null;
  createdAt: string;
  canReprocess?: boolean;
  spamScore?: number | null;
  spamSignals?: SpamSignal[];
}

export interface ReprocessResult {
  ok: boolean;
  status: string;
  error?: string | null;
  ticketId?: string | null;
  ticketNumber?: string | null;
}

export const emailApi = {
  getIntegration: async (): Promise<EmailIntegration | null> => {
    const res = await apiClient.get('/email/integration');
    return (res.data.data.integration as EmailIntegration | null) ?? null;
  },

  saveIntegration: async (data: SaveEmailIntegrationInput): Promise<EmailIntegration> => {
    const res = await apiClient.post('/email/integration', data);
    return res.data.data.integration as EmailIntegration;
  },

  listLogs: async (page = 1, status?: string): Promise<{ logs: InboundEmailLogRow[]; total: number; page: number; limit: number }> => {
    const res = await apiClient.get('/email/logs', { params: { page, ...(status && { status }) } });
    return res.data.data;
  },

  /** `notSpam` replays a quarantined message with spam scoring bypassed —
   *  without it the same verdict would just quarantine it again. */
  reprocessLog: async (id: string, notSpam = false): Promise<ReprocessResult> => {
    const res = await apiClient.post(`/email/logs/${id}/reprocess`, { notSpam });
    return res.data.data.result as ReprocessResult;
  },
};
